<template>
  <div>
    <div class="table-header d-flex">
      <v-text-field
        class=""
        block
        v-model="search"
        rounded
        outlined
        dense
        clearable
        prepend-inner-icon="mdi-magnify"
        :placeholder="$t('search_segment')"
      ></v-text-field>
      <v-spacer></v-spacer>
      <v-tooltip bottom dark color="#333333" v-if="is_manual">
        <template v-slot:activator="{ on }">
          <v-btn
            class="mb-1"
            @click="showAddMarketSegment"
            rounded
            color="secondary"
            v-on="on"
          >
            <v-icon small>mdi-plus</v-icon>
            {{ $t("add_segment") }}
          </v-btn>
        </template>
        <span>
          {{ $t("create_segment") }}
        </span>
      </v-tooltip>
      <div v-else>
        <v-icon>mdi-information-outline</v-icon>
        {{ $t("pms_generated_segments") }}
      </div>
    </div>
    <v-data-table
      v-if="filtered_market_segments && filtered_market_segments.length > 0"
      :headers="market_segments_headers"
      :items="filtered_market_segments"
      hide-default-footer
      disable-pagination
      @click:row="showEditMarketSegment"
      :item-class="getDatatableRowClass"
      class="market-segments-table"
      v-sortable-data-table
      @sorted="saveOrder"
    >
      <template v-slot:[`item.move`]>
        <div class="drag-icon">
          <v-icon>mdi-drag-vertical</v-icon>
        </div>
      </template>
      <template v-slot:[`item.name`]="{ item }">
        <v-avatar size="10" class="mr-2" :color="item.color"></v-avatar>
        <b class="mr-1">{{ item.name }}</b>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <template v-if="item.visible">
          <v-chip small color="secondary">{{ $t("visible") }}</v-chip>
        </template>
        <template v-else>
          <v-chip small>{{ $t("hidden") }}</v-chip>
        </template>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip
          v-if="item.can_delete && is_manual"
          bottom
          dark
          color="#333333"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              @click.stop="showDeleteMarketSegment(item)"
              icon
              color="secondary"
              v-on="on"
              ><v-icon small>mdi-trash-can-outline</v-icon></v-btn
            >
          </template>
          <span>
            {{ $t("delete") }}
          </span>
        </v-tooltip>
        <v-tooltip v-else-if="!item.can_delete" bottom dark color="#333333">
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-lock-outline</v-icon>
          </template>
          <span>
            {{ $t("segment_locked") }}
          </span>
        </v-tooltip>
      </template>
    </v-data-table>

    <!-- Edit Market Segment -->
    <v-dialog
      v-model="show_edit_market_segment"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="400"
    >
      <v-card>
        <v-card-title
          v-if="item_to_edit.id"
          center
          class="headline justify-center py-6"
        >
          {{ $t("edit_segment") }}
        </v-card-title>
        <v-card-title v-else center class="headline justify-center py-6">
          {{ $t("new_segment") }}
        </v-card-title>
        <v-card-text class="text-center justify-center" v-if="item_to_edit">
          <v-form
            ref="edit_market_segment_form"
            v-model="can_set_market_segment"
          >
            <v-row>
              <v-col cols="12" class="py-0">
                <v-text-field
                  v-model="item_to_edit.name"
                  maxlength="50"
                  counter
                  :label="$t('name')"
                  :rules="[v => !!v || $t('required')]"
                  :placeholder="$t('name')"
                  outlined
                  rounded
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="py-0">
                <v-text-field
                  v-model="item_to_edit.code"
                  maxlength="50"
                  counter
                  :label="$t('code')"
                  :rules="[v => !!v || $t('required')]"
                  :placeholder="$t('code')"
                  outlined
                  rounded
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4" class="py-0 text-left">
                <label>{{ $t("is_visible") }}</label>
                <v-switch v-model="item_to_edit.visible"></v-switch>
              </v-col>
              <v-col v-if="!isAdd" cols="8" class="py-0 text-left">
                <label class="d-block mb-3">{{ $t("color") }}</label>
                <v-menu
                  v-model="market_segment_color_menu"
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ on }">
                    <div
                      class="swatch-style"
                      :style="{ backgroundColor: item_to_edit.color }"
                      v-on="on"
                    ></div>
                  </template>
                  <v-color-picker
                    v-model="item_to_edit.color"
                    mode="hexa"
                  ></v-color-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pt-0 pb-5 justify-center">
          <v-btn
            v-if="item_to_edit.id"
            color="secondary"
            rounded
            depressed
            class="pl-5 pr-5"
            @click="setMarketSegment()"
            :disabled="!can_set_market_segment"
          >
            {{ $t("set_segment") }}
          </v-btn>
          <v-btn
            v-else
            color="secondary"
            rounded
            depressed
            class="pl-5 pr-5"
            @click="addMarketSegment()"
            :disabled="!can_set_market_segment"
          >
            {{ $t("add_segment") }}
          </v-btn>
          <v-btn
            color=""
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="hideEditMarketSegment"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELETE Market Segment -->
    <v-dialog
      v-model="show_delete_market_segment"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="400"
    >
      <v-card>
        <v-card-title center class="headline justify-center"
          >{{ $t("are_you_sure") }}
        </v-card-title>
        <v-card-text
          class="text-center justify-center"
          v-if="item_to_edit"
          v-html="$t('sure_delete_segment', { segment: item_to_edit.name })"
        >
        </v-card-text>
        <v-card-actions class="ps-5 pb-5 justify-center">
          <v-btn
            color="red"
            rounded
            depressed
            text
            class="pl-5 pr-5"
            @click="deleteMarketSegment()"
          >
            {{ $t("delete") }}
          </v-btn>
          <v-btn
            color=""
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="hideDeleteMarketSegment"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import * as Utils from "@/utils.js";
import Sortable from "sortablejs";

export default {
  name: "MarketSegments",
  components: {},
  data: () => ({
    item_to_edit: {},
    show_delete_market_segment: false,
    show_edit_market_segment: false,
    can_set_market_segment: null,
    market_segment_color_menu: false,
    isAdd: false,
    search: ""
  }),
  props: {
    market_segments: {
      type: Array,
      default: function() {
        return [];
      },
      required: true
    }
  },
  directives: {
    sortableDataTable: {
      bind(el, binding, vnode) {
        const options = {
          animation: 150,
          onUpdate: function(event) {
            vnode.child.$emit("sorted", event);
          }
        };
        Sortable.create(el.getElementsByTagName("tbody")[0], options);
      }
    }
  },
  methods: {
    getDatatableRowClass() {
      return "a-datatable-row market-segment-row";
    },
    clearEditForm() {
      if (this.$refs.edit_market_segment_form) {
        this.$refs.edit_market_segment_form.resetValidation();
      }
    },
    showAddMarketSegment() {
      this.clearEditForm();
      this.isAdd = true;
      this.show_edit_market_segment = true;
      this.item_to_edit = {
        id: null,
        hotel_id: this.current_hotel.id ? this.current_hotel.id : null
      };
    },
    showEditMarketSegment(market_segment) {
      if (!market_segment) {
        return;
      }
      this.isAdd = false;
      this.clearEditForm();
      this.item_to_edit = JSON.parse(JSON.stringify(market_segment));
      this.show_edit_market_segment = true;
    },
    hideEditMarketSegment() {
      this.show_edit_market_segment = false;
      this.item_to_edit = {};
    },
    setMarketSegment: function() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("modifying_segment"),
        color: "secondary"
      });
      this.item_to_edit.visible = this.item_to_edit.visible ? true : false;
      this.$store
        .dispatch("setMarketSegment", this.item_to_edit)
        .then(() => {
          this.show_edit_market_segment = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("segment_modified"),
            color: "accent"
          });
          this.item_to_edit = {};
          this.getMarketSegments();
        })
        .catch(() => {
          this.show_edit_market_segment = false;
          this.item_to_edit = {};
          Utils.showError(this, "segment_not_modified");
        });
    },
    addMarketSegment: function() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("adding_segment"),
        color: "secondary"
      });
      this.item_to_edit.visible = this.item_to_edit.visible ? true : false;
      this.$store
        .dispatch("setMarketSegment", this.item_to_edit)
        .then(() => {
          this.show_edit_market_segment = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("segment_added"),
            color: "accent"
          });
          this.item_to_edit = {};
          this.getMarketSegments();
        })
        .catch(() => {
          this.show_edit_market_segment = false;
          this.item_to_edit = {};
          Utils.showError(this, "segment_not_added");
        });
    },
    showDeleteMarketSegment(market_segment) {
      if (!market_segment) {
        return;
      }
      this.show_delete_market_segment = true;
      this.item_to_edit = market_segment;
    },
    hideDeleteMarketSegment() {
      this.show_delete_market_segment = false;
      this.item_to_edit = {};
    },
    deleteMarketSegment: function() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("deleting_segment"),
        color: "secondary"
      });
      this.$store
        .dispatch("deleteMarketSegment", {
          hotel_id: this.current_hotel.id,
          id: this.item_to_edit.id
        })
        .then(() => {
          this.show_delete_market_segment = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("segment_deleted"),
            color: "accent"
          });
          this.item_to_edit = {};
          this.getMarketSegments();
        })
        .catch(() => {
          this.show_delete_market_segment = false;
          this.item_to_edit = {};
          Utils.showError(this, "segment_not_deleted");
        });
    },
    saveOrder(event) {
      const newIndex = event.newIndex + 1;
      const movedItem = this.filtered_market_segments.splice(
        event.oldIndex,
        1
      )[0];
      this.filtered_market_segments.splice(event.newIndex, 0, movedItem);
      this.$store.dispatch("setSnackbar", {
        title: this.$t("setting_segment_position"),
        color: "secondary"
      });
      this.$store
        .dispatch("setMarketSegmentPosition", {
          id: movedItem.id,
          position: newIndex
        })
        .then(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("segment_position_changed"),
            color: "accent"
          });
          this.getMarketSegments();
        })
        .catch(() => {
          Utils.showError(this, "segment_position_not_changed");
        });
    },
    getMarketSegments() {
      this.$emit("getMarketSegments");
    }
  },
  computed: {
    filtered_market_segments() {
      let market_segments = this.market_segments;
      if (this.search && this.search.length > 0) {
        market_segments = market_segments.filter(c =>
          c.name.toLowerCase().includes(this.search.toLowerCase())
        );
      }
      return market_segments;
    },
    market_segments_headers() {
      return [
        {
          text: "",
          align: "left",
          sortable: false,
          value: "move",
          width: "40px"
        },
        {
          text: this.$t("name"),
          align: "left",
          sortable: false,
          value: "name"
        },
        {
          text: this.$t("status"),
          align: "center",
          sortable: false,
          value: "status"
        },
        {
          text: null,
          align: "center",
          sortable: false,
          value: "actions",
          width: "120px"
        }
      ];
    },
    is_manual() {
      return (
        this.current_hotel &&
        this.current_hotel.settings &&
        this.current_hotel.settings.pms &&
        this.current_hotel.settings.pms === "manual"
      );
    },
    ...mapState({
      segments_store: state => state.segmentschannels
    }),
    ...mapGetters({
      current_hotel: "current_hotel",
      l: "current_hotel_language"
    })
  }
};
</script>

<style lang="scss" scoped>
.market-segments-table .market-segment-row > td:last-child {
  display: flex;
  align-items: center;
  padding-right: 55px;
  justify-content: flex-end;
}
.market-segments-table .market-segment-row .drag-icon {
  display: inline-flex;
  width: 20px;
  i {
    cursor: move;
  }
}
.swatch-style {
  margin-top: -16px;
  border-radius: 7px;
  cursor: pointer;
  height: 30px;
  width: 30px;
}
</style>
